/* @noflow FIXME for real, I don't think the Notice works */
import * as React from 'react';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { debounce } from 'lodash';

import { bindActionCreators } from 'redux';
import { orgTypeString as ORG_TYPE_STRING } from 'constants/SelfServeConstants';
import { REQUESTER_ROLE_OPTIONS } from 'admin/constants/AdminUpgrade';
import { saveLocalQuoteField } from 'admin/reducers/adminUpgradePage';
import assertions from 'lib/assertions';
import Columns2 from 'components/Columns2';
import Countries, { regionsForCountry, stateIsValid, countryIsValid } from 'lib/geo/Country';
import DatePickerInput from 'components/DatePickerInput';
import EventTracking from 'lib/EventTracking';
import Field from 'components/Field';
import FormElement from 'components/FormElement';
import Glyph from 'components/Glyph';
import Heading from 'components/Heading';
import Input from 'components/Input';
import InputValueConstants from 'constants/InputValueConstants';
import isSignedIn from 'user/queries/isSignedInQuery.gql';
import Label from 'components/Label';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import Notice from 'components/Notice';
import SchoolOrDistrictPicker from 'components/SchoolOrDistrictPicker/SchoolOrDistrictPicker';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput';
import Radio from 'components/Radio';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import Tooltip from 'components/Tooltip';
import type { Fields } from 'admin/reducers/adminUpgradePage';

import './styles/AdminUpgradePlanDefinitionForm.scss';

function StartDateHelpIcon() {
  const tooltip = (
    <Tooltip>
      Remind Plans are annual.
      <br />
      Select the start of the billing cycle.
    </Tooltip>
  );

  return (
    <LayerTrigger placement="top" layer={tooltip}>
      <Glyph name="help" />
    </LayerTrigger>
  );
}

function AddressHelpIcon() {
  const tooltip = (
    <Tooltip>
      The organization’s
      <br />
      physical address.
    </Tooltip>
  );

  return (
    <LayerTrigger placement="top" layer={tooltip}>
      <Glyph name="help" />
    </LayerTrigger>
  );
}

export const ORG_RADIOS = [
  {
    key: 'school',
    name: 'School',
  },
  {
    key: 'district',
    name: 'District',
  },
  {
    key: 'other',
    name: 'Other',
  },
];

type Props = {
  ...isSignedInQuery,
  fields: Fields,
  onSelectSchool?: () => void,
  organizationUuid: ?string,
  orgTypeString: ?string,
  saveLocalQuoteField: (name: string, value: *) => void,
};

type State = {
  orgTypeError: ?string,
  today: Date,
  studentEnrollmentFieldValue: ?number,
  showCountryField: boolean,
};

class AdminUpgradePlanDefinitionForm extends React.Component<Props, State> {
  state: State = {
    today: new Date(),
    studentEnrollmentFieldValue: null,
  };

  static trackingPrefix = 'admin_self_serve.define_plan';

  componentDidMount() {
    const { organizationUuid, fields } = this.props;

    if (organizationUuid && !fields.get('organizationUuid')) {
      this.props.saveLocalQuoteField('organizationUuid', organizationUuid);
      this.track('org_type.click');
    }

    if (!fields.get('startDate')) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.props.saveLocalQuoteField('startDate', tomorrow);
    }

    if (!fields.get('country')) {
      this.props.saveLocalQuoteField('country', Countries[0].value);
    }

    this.props.saveLocalQuoteField('latitude', null);
    this.props.saveLocalQuoteField('longitude', null);
    this.track('view');
  }

  componentWillUnmount() {
    const { fields, saveLocalQuoteField: saveField } = this.props;
    function prefillBillingField(requesterFieldName: string, billingFieldName: string) {
      if (!fields.get(billingFieldName)) {
        saveField(billingFieldName, fields.get(requesterFieldName));
      }
    }

    prefillBillingField('requesterFirstName', 'billingFirstName');
    prefillBillingField('requesterLastName', 'billingLastName');
    prefillBillingField('requesterEmail', 'billingEmail');
    prefillBillingField('requesterPhoneNumber', 'billingPhoneNumber');
  }

  handleOrgAddressFocus = () => {
    this.track('organization_address.click');

    if (this.props.fields.get('orgType')) {
      return;
    }

    this.setState({ orgTypeError: 'You must select an organization type.' });
  };

  track = (key: string) => {
    EventTracking.trackKey(`${AdminUpgradePlanDefinitionForm.trackingPrefix}.${key}`);
  };

  setOrgType = (type: string) => {
    const { fields } = this.props;

    this.props.saveLocalQuoteField('orgType', type);

    const orgType = fields.get('orgType');

    if (orgType && orgType !== type && (orgType === 'district' || type === 'district')) {
      this.props.saveLocalQuoteField('organizationUuid', null);
      this.props.saveLocalQuoteField('organizationAddress', null);
    }

    this.setState({ orgTypeError: null });
  };

  render() {
    const { fields, onSelectSchool } = this.props;
    const { orgTypeError } = this.state;

    const orgType = fields.get('orgType');
    const country = fields.get('country');
    const regionLabel = regionsForCountry[country] ? regionsForCountry[country].label : 'Region';

    const trackStartDateClick = debounce(() => {
      this.track('start_date.click');
    }, 100);

    return (
      <div>
        <Field error={orgTypeError}>
          <Label>Organization type</Label>
          {ORG_RADIOS.map(o => (
            <Radio
              inline
              name="org"
              key={o.key}
              label={o.name}
              onChange={() => this.setOrgType(o.key)}
              checked={orgType === o.key}
              onFocus={() => this.track('org_type.click')}
              data-test-selector={`radio-org-type-${o.key}`}
            />
          ))}
        </Field>
        {orgType && (
          <FormElement
            key={orgType}
            label={this.props.orgTypeString}
            onValidChange={o => {
              this.track('select_school.click');
              if (o.country && countryIsValid(o.country)) {
                this.props.saveLocalQuoteField('country', o.country);
              }
              if (o.state && stateIsValid(o.state, o.country)) {
                this.props.saveLocalQuoteField('state', o.state);
              }
              if (o.city) {
                this.props.saveLocalQuoteField('city', o.city);
              }
              this.props.saveLocalQuoteField('organizationUuid', o.uuid);
              this.props.saveLocalQuoteField('latitude', o.latitude);
              this.props.saveLocalQuoteField('longitude', o.longitude);

              if (fields.get('orgType') === 'school' && onSelectSchool) {
                onSelectSchool(o.uuid);
              }
            }}
            element={SchoolOrDistrictPicker}
            elementProps={{
              type: orgType === 'district' ? 'district' : 'school',
              onClickInteraction: () => this.track('school_picker.click'),
              initialOrganizationUuid: fields.get('organizationUuid'),
            }}
          />
        )}
        <FormElement
          label="Country"
          validate={value => {
            assertions.notEmpty(value, 'Please select a country');
          }}
          onValidChange={countryValue => this.props.saveLocalQuoteField('country', countryValue)}
          onInvalidChange={() => this.props.saveLocalQuoteField('country', undefined)}
          element={Select}
          elementProps={{
            name: 'country',
            value: country,
            options: Countries,
            className: 'AdminUpgradePlanDefinitionForm-countryPicker',
          }}
        />
        <Columns2>
          <FormElement
            label={
              <span>
                {this.props.orgTypeString
                  ? `${this.props.orgTypeString} street address`
                  : 'Street address'}{' '}
                <AddressHelpIcon />
              </span>
            }
            validate={address => {
              assertions.notEmpty(address);
            }}
            onValidChange={e => this.props.saveLocalQuoteField('streetAddress', e.target.value)}
            onInvalidChange={() => this.props.saveLocalQuoteField('streetAddress', undefined)}
            element={Input}
            elementProps={{
              onFocus: this.handleOrgAddressFocus,
              value: fields.get('streetAddress'),
              placeholder: '1234 Remind Road',
              'data-test-selector': 'street-address-input',
            }}
          />
          <FormElement
            label="City"
            validate={city => {
              assertions.notEmpty(city);
            }}
            onValidChange={e => this.props.saveLocalQuoteField('city', e.target.value)}
            onInvalidChange={() => this.props.saveLocalQuoteField('city', undefined)}
            element={Input}
            elementProps={{
              onFocus: this.handleOrgAddressFocus,
              value: fields.get('city'),
              'data-test-selector': 'city-input',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label={regionLabel}
            onValidChange={state => this.props.saveLocalQuoteField('state', state)}
            onInvalidChange={() => this.props.saveLocalQuoteField('state', undefined)}
            validate={value => {
              assertions.notEmpty(value, `Please select a ${regionLabel.toLowerCase()}`);
            }}
            element={Select}
            elementProps={{
              name: 'state',
              value: fields.get('state'),
              options: regionsForCountry[country] ? regionsForCountry[country].regions : [],
              disabled: !country,
              className: 'AdminUpgradePlanDefinitionForm-regionPicker',
            }}
          />
          <FormElement
            label="Postal code"
            validate={postalCode => {
              assertions.notEmpty(postalCode);
            }}
            onValidChange={e => this.props.saveLocalQuoteField('postalCode', e.target.value)}
            onInvalidChange={() => this.props.saveLocalQuoteField('postalCode', undefined)}
            element={Input}
            elementProps={{
              onFocus: this.handleOrgAddressFocus,
              value: fields.get('postalCode'),
              'data-test-selector': 'postal-code-input',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label="Estimated teachers on staff"
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
            }}
            onValidChange={e => this.props.saveLocalQuoteField('teachersCount', e.target.value)}
            onInvalidChange={() => this.props.saveLocalQuoteField('teachersCount', undefined)}
            element={Input}
            elementProps={{
              onFocus: () => this.track('teachers_on_staff.click'),
              defaultValue: fields.get('teachersCount'),
              value: fields.get('teachersCount'),
              'data-test-selector': 'est-teachers-input',
            }}
          />
          <FormElement
            label="Estimated student enrollment"
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
              assertions.valueGreaterThan(5, e.target.value, 'Must include more than five people');
            }}
            onValidChange={e => this.props.saveLocalQuoteField('studentsCount', e.target.value)}
            onInvalidChange={() => this.props.saveLocalQuoteField('studentsCount', undefined)}
            element={Input}
            elementProps={{
              onFocus: () => {
                this.track('student_enrollment.click');
              },
              onBlur: e => {
                this.setState({
                  studentEnrollmentFieldValue: e.target.value,
                });
              },
              value: fields.get('studentsCount'),
              'data-test-selector': 'est-students-input',
            }}
          />
        </Columns2>
        {this.state.studentEnrollmentFieldValue && this.state.studentEnrollmentFieldValue <= 5 && (
          <Row size="xxxlarge">
            <Notice color="polar-bear" noShadow data-test="student-enrollment-too-small">
              <Text size="xsmall">
                Remind plans are only available for schools and districts, but you can always start
                with an individual account! Click <Link to="/">here</Link> to sign up for free.
              </Text>
            </Notice>
          </Row>
        )}
        <Columns2>
          <FormElement
            label={
              <span>
                Plan start date <StartDateHelpIcon />
              </span>
            }
            validate={date => {
              assertions.withinSixMonths(
                date,
                'Please select a billing start date within the next 6 months.'
              );
            }}
            onValidChange={date => this.props.saveLocalQuoteField('startDate', date)}
            element={DatePickerInput}
            elementProps={{
              onFocus: trackStartDateClick,
              onClick: trackStartDateClick,
              value: fields.get('startDate'),
              min: this.state.today,
              full: true,
              'data-test-selector': 'start-date-input',
            }}
          />
        </Columns2>
        <section className="AdminUpgradePlanDefinitionForm-about">
          <Row size="small">
            <Heading level="2" levelStyle="6" divided>
              About You
            </Heading>
          </Row>
          {!this.props.user && (
            <Columns2>
              <FormElement
                label="First name"
                validate={e => {
                  assertions.notEmpty(e.target.value);
                  assertions.lengthLesserThanOrEqualTo(
                    e.target.value,
                    InputValueConstants.FIRST_NAME_LENGTH
                  );
                }}
                onValidChange={e =>
                  this.props.saveLocalQuoteField('requesterFirstName', e.target.value)
                }
                onInvalidChange={() =>
                  this.props.saveLocalQuoteField('requesterFirstName', undefined)
                }
                element={Input}
                elementProps={{
                  onFocus: () => this.track('requester_first_name.click'),
                  defaultValue: fields.get('requesterFirstName'),
                  'data-test-selector': 'first-name-input',
                }}
              />
              <FormElement
                label="Last name"
                validate={e => {
                  assertions.notEmpty(e.target.value);
                  assertions.lengthLesserThanOrEqualTo(
                    e.target.value,
                    InputValueConstants.LAST_NAME_LENGTH
                  );
                }}
                onValidChange={e =>
                  this.props.saveLocalQuoteField('requesterLastName', e.target.value)
                }
                onInvalidChange={() =>
                  this.props.saveLocalQuoteField('requesterLastName', undefined)
                }
                element={Input}
                elementProps={{
                  onFocus: () => this.track('requester_last_name.click'),
                  defaultValue: fields.get('requesterLastName'),
                  'data-test-selector': 'last-name-input',
                }}
              />
            </Columns2>
          )}
          <Columns2>
            <FormElement
              label="Role"
              validate={role => {
                assertions.notEmpty(role);
              }}
              onValidChange={role => this.props.saveLocalQuoteField('requesterRole', role)}
              onInvalidChange={() => this.props.saveLocalQuoteField('requesterRole', undefined)}
              element={Select}
              elementProps={{
                onFocus: () => this.track('requester_role.click'),
                options: REQUESTER_ROLE_OPTIONS,
                value: fields.get('requesterRole'),
                className: 'AdminUpgradePlanDefinitionForm-rolePicker',
              }}
            />
          </Columns2>
          <Columns2>
            <FormElement
              label="Your phone number"
              validate={e => {
                assertions.number(e.raw, 'Invalid phone number.');
                assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
              }}
              onValidChange={e => this.props.saveLocalQuoteField('requesterPhoneNumber', e.raw)}
              onInvalidChange={() =>
                this.props.saveLocalQuoteField('requesterPhoneNumber', undefined)
              }
              element={PhoneNumberMaskedInput}
              elementProps={{
                defaultValue: fields.get('requesterPhoneNumber'),
                onFocus: () => this.track('requester_phone_number.click'),
                'data-test-selector': 'phone-number-input',
              }}
            />
            {!this.props.user && (
              <FormElement
                showErrorAfterBlur
                label="Your official email address"
                validate={e => {
                  assertions.email(e.target.value);
                  assertions.notEmpty(e.target.value);
                }}
                onValidChange={e =>
                  this.props.saveLocalQuoteField('requesterEmail', e.target.value)
                }
                onInvalidChange={() => this.props.saveLocalQuoteField('requesterEmail', undefined)}
                element={Input}
                elementProps={{
                  onFocus: () => this.track('requester_email.click'),
                  defaultValue: fields.get('requesterEmail'),
                  'data-test-selector': 'email-input',
                }}
              />
            )}
          </Columns2>
        </section>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    fields: state.adminUpgradePage.fields,
    orgTypeString: ORG_TYPE_STRING[state.adminUpgradePage.fields.get('orgType')],
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ saveLocalQuoteField }, dispatch);
}

const withData = graphql(isSignedIn, {
  props: ({ data }) => ({ user: data.me, error: data.error }),
});

export { AdminUpgradePlanDefinitionForm };
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withData(AdminUpgradePlanDefinitionForm));
